/* eslint-disable camelcase */
import {
    DATA_STORE_CONNECTION_STATUS,
    FIELD_VISIBILITY_TYPES,
    TYPE_ON_PREM_DATASET,
    TYPE_ON_PREM_FIELD,
    TYPE_ON_PREM_SCHEMA,
    TYPE_SAAS_DATASET,
    TYPE_SAAS_DATASET_ALT,
    TYPE_SAAS_FIELD,
    TYPE_SAAS_FIELD_SET,
    TYPE_SAAS_SCHEMA,
} from '/b2b/dataStores/constants';

import castArray from 'lodash/castArray';
import { formatUser } from '/b2b/users/format';
import { makeResultsFormatter } from '../common/services/helpers';
import set from 'lodash/set';
import snakeCase from 'lodash/snakeCase';
import { translate } from '/b2b/common/helpers/i18n';

export const formatDataProvider = data => {
    const {
        provider_id,
        providerId = provider_id,
        provider_name,
        name = provider_name,
        provider_description,
        description = provider_description,
        provider_type = 'saas',
        type = provider_type, //can be 'saas', 'internal', or 'onprem'
    } = data;

    return {
        providerId,
        name,
        description,
        type,
    };
};

export const formatDataStoreAgent = data => {
    const {
        agent_id,
        agentId = agent_id,
        agent_name,
        name = agent_name,
        agent_description,
        description = agent_description,
        agent_type,
        type = agent_type,
        agent_version,
        version = agent_version,
        agent_schema,
        schema = agent_schema,
        agent_created,
        agent_on_prem, // Deprecated, use provider_type
        created = agent_created,
        agent_updated,
        updated = agent_updated,

        provider_id,
        provider_name,
        provider_description,
        provider_type,
        provider: providedProvider,
        documentation_url,
        documentationUrl = documentation_url,
        onPrem = agent_on_prem,
    } = data;

    const provider = formatDataProvider({
        providerId: provider_id,
        name: provider_name,
        description: provider_description,
        type: provider_type,
        ...providedProvider,
    });

    return {
        agentId,
        type,
        // TODO: remove post DISCO / DSAR OKR
        authType: type,
        name,
        description,
        schema,
        version,
        onPrem: onPrem || provider.type === 'onprem',
        created,
        updated,
        provider,
        documentationUrl,
    };
};
export const formatDataStoreAgentResults = makeResultsFormatter(formatDataStoreAgent);

export const formatDataStore = data => {
    const {
        actions_to_complete = [],
        actionsToComplete = actions_to_complete,

        agent_id,
        agent_name,
        agent_description,
        agent_version,
        agent_schema,
        agent_on_prem,
        agent_created,
        agent_updated,
        agent_type,
        agent: providedAgent,

        assessments,

        available_connection,
        availableConnection = available_connection,
        available_integration,
        availableIntegration = available_integration,

        provider_id,
        provider_name,
        provider_description,
        provider_type,
        provider: providedProvider,

        customer_id,
        customerId = customer_id,

        name,
        description,

        connection_id,
        connectionId = connection_id,
        connection_description,
        connectionDescription = connection_description,
        data_store_id,
        dataStoreId = data_store_id,
        connection_name,
        connectionName = connection_name,
        connection_nickname,
        nickname = connection_nickname,
        connection_status,
        status = connection_status,
        connection_status_message,
        statusMessage = connection_status_message,
        connection_last_sync,
        lastSync = connection_last_sync,
        connection_samples_url,
        samplesUrl = connection_samples_url,
        connection_total_fields,
        totalFieldCount = connection_total_fields,
        connection_unclassified_fields,
        unclassifiedFieldCount = connection_unclassified_fields,
        connection_credentials,
        credentials = connection_credentials,
        connection_created,
        created = connection_created,
        connection_updated,
        updated = connection_updated,
        connection_owners = [],
        connection_primary_owners = [],
        owners: providedAssignees = connection_owners,
        primaryOwners: providedOwners = connection_primary_owners,
        automation_settings,
        automationSettings = automation_settings,
        provider_search_enabled,
        isProviderSearchEnabled = provider_search_enabled,
        provider_deletion_enabled,
        isProviderDeletionEnabled = provider_deletion_enabled,
        product,
        vendor,
        sso_applications = [],
        ssoApplications = sso_applications,
        source_connections = [],
        sourceConnections = source_connections,
        linked_apps,
        linkedApps = linked_apps,
        linked_tasks = [],
        linkedTasks = linked_tasks,
        organizations = [],
        organization_owners = [],
        organizationOwners = organization_owners,
        location = {},
        labels = [],
        privacy_risk_score,
        privacyRiskScore = privacy_risk_score,
        purposes_of_processing = [],
        purposesOfProcessing = purposes_of_processing,
        assessment_fields = [],
        assessmentFields = assessment_fields,
    } = data;

    const provider = {
        providerId: provider_id,
        name: provider_name,
        description: provider_description,
        type: provider_type,
        ...providedProvider,
    };

    const agent = {
        agentId: agent_id,
        name: agent_name,
        description: agent_description,
        authType: agent_type, // TODO: Deprecated, please remove after Data Store migration
        type: agent_type,
        version: agent_version,
        schema: agent_schema,
        onPrem: agent_on_prem,
        created: agent_created,
        updated: agent_updated,
        provider,
        ...providedAgent,
    };

    const lookup = {};
    const assignees = castArray(providedAssignees)
        .filter(o => {
            const { email } = o || {};
            const isUnique = email && !lookup[`${email}`];
            if (isUnique) {
                lookup[`${email}`] = true;
                return true;
            }
            return false;
        })
        .map(formatUser);

    const primaryOwners = assignees.filter(({ is_primary }) => is_primary);
    const owners = providedOwners.map(formatUser);
    const orgOwners = organizationOwners.map(formatUser);
    const defaultAssignees = primaryOwners;
    return {
        actionsToComplete,
        agent: formatDataStoreAgent(agent),
        assessments,
        assessmentFields,
        assignees,
        availableConnection,
        availableIntegration,
        customer: customerId,
        connectionDescription,
        connectionId,
        connectionName,
        created,
        credentials,
        dataStoreId,
        defaultAssignees: defaultAssignees ? formatUser(defaultAssignees) : orgOwners,
        description,
        lastSync,
        linkedApps,
        linkedTasks,
        labels,
        location,
        name,
        nickname,
        organizations,
        organizationOwners: orgOwners,
        owners,
        primaryOwners,
        privacyRiskScore,
        purposesOfProcessing,
        samplesUrl,
        ssoApplications,
        sourceConnections,
        status,
        statusMessage,
        totalFieldCount: parseInt(totalFieldCount, 10) || 0,
        unclassifiedFieldCount,
        updated,
        automationSettings,
        isProviderSearchEnabled,
        isProviderDeletionEnabled,
        product,
        vendor,
    };
};

export const formatDataStoreResults = makeResultsFormatter(formatDataStore);

export const formatOwner = owner => {
    return {
        ...owner,
        name: `${owner.first_name} ${owner.last_name}`,
        email: owner.email,
        isPrimary: owner.is_primary,
        isOrgOwner: owner.is_org_owner,
    };
};

const formatDataStoreConnectionSamplesLegacy = schema => {
    const parsedSchema = {};

    function traverse(item, path) {
        if (item) {
            if (Array.isArray(item)) {
                item.forEach(value => traverse(value, path));
            } else if (typeof item === 'object') {
                const { data, metadata = {} } = item;
                const { dataType, name, piiType, samples, type } = metadata;

                const isSchema = [TYPE_ON_PREM_SCHEMA, TYPE_SAAS_SCHEMA].includes(type);
                const isDataset = [
                    TYPE_ON_PREM_DATASET,
                    TYPE_SAAS_DATASET,
                    TYPE_SAAS_DATASET_ALT,
                    TYPE_SAAS_FIELD_SET,
                ].includes(dataType);
                const isField = [TYPE_ON_PREM_FIELD, TYPE_SAAS_FIELD].includes(type) || !!piiType;

                if (isSchema || isDataset || isField) {
                    path = [...path.split('.'), snakeCase(name)].filter(Boolean).join('.');

                    if (isField) {
                        return set(parsedSchema, path, samples || []);
                    }
                }

                return traverse(data, path);
            }
        }
    }

    traverse(schema, '');

    return parsedSchema;
};

export const formatDataStoreConnectionSamples = schema => {
    if (Object.prototype.hasOwnProperty.call(schema, 'data') && Array.isArray(schema['data'])) {
        // to be removed once objects are reformatted
        return formatDataStoreConnectionSamplesLegacy(schema);
    } else {
        return schema;
    }
};

export const formatDataStoreClassification = classification => classification;
export const formatDataStoreClassificationResults = makeResultsFormatter(
    formatDataStoreClassification
);

export const formatDataStoreField = data => {
    const {
        field_id,
        fieldId = field_id,
        field_name,
        name = field_name,
        field_notes,
        notes = field_notes,
        field_classifications,
        classifications = field_classifications,
        field_classification = classifications[0],
        classification = field_classification,
        field_samples_path,
        samplesPath = field_samples_path,
        field_description,
        description = field_description,
        field_path,
        path = field_path,
        field_manually_classified,
        manuallyClassified = field_manually_classified,
        field_data_found,
        dataFound = field_data_found,
        field_classified_by,
        classifiedBy = field_classified_by,
        field_classified_date,
        classifiedDate = field_classified_date,
        field_agent_classification,
        agentClassification = field_agent_classification,
        field_hidden,
        hidden = field_hidden,
        field_redact,
        redact = field_redact,
        field_regex_classification,
        regexClassification = field_regex_classification,
        field_content_classification,
        contentClassification = field_content_classification,
        field_content_classification_scores,
        contentClassificationScores = field_content_classification_scores,
        ...remain
    } = data;

    const visibility = hidden
        ? FIELD_VISIBILITY_TYPES.HIDDEN
        : redact
          ? FIELD_VISIBILITY_TYPES.REDACT
          : FIELD_VISIBILITY_TYPES.NONE;

    return {
        ...remain,
        agentClassification,
        classification: formatDataStoreClassification(classification),
        classifiedBy,
        classifiedDate,
        contentClassification,
        contentClassificationScores,
        dataFound,
        description,
        fieldId,
        manuallyClassified,
        name,
        notes,
        path,
        regexClassification,
        samplesPath,
        visibility,
    };
};
export const formatDataStoreFieldResults = makeResultsFormatter(formatDataStoreField);

const formatDataStoreRelationships = ({ targetName, ...data }) => ({
    ...data,
    targetName,
    name: targetName,
});

export const formatDataStoreRelationshipsResults = makeResultsFormatter(
    formatDataStoreRelationships
);

const formatName = name => name || '—';

export const formatStatus = (status, providerType) => {
    if (providerType === 'internal') {
        return translate('components.DataProviderList.syncStatus.manual');
    }
    return translate(DATA_STORE_CONNECTION_STATUS[status] || status);
};

const formatOrganizationsColumn = organizations =>
    organizations ? organizations.map(org => org.name).join(', ') : '—';

const formatOwners = owners =>
    Array.isArray(owners) && owners.length > 0
        ? owners.map(owner => owner.email || owner.name).join(', ')
        : '—';

const formatSources = sourceConnections =>
    Array.isArray(sourceConnections) && sourceConnections.length > 0
        ? sourceConnections.map(source => source.name).join(', ')
        : '—';

export const formatCompletion = actionsToComplete => {
    if (!Array.isArray(actionsToComplete) || actionsToComplete.length === 0) {
        return '0%';
    }

    const completedCount = actionsToComplete.filter(({ completed }) => completed).length;
    const percentage = (completedCount / actionsToComplete.length) * 100;

    return `${Math.round(percentage)}%`;
};

const formatAssessments = assessments =>
    Array.isArray(assessments) && assessments.length > 0
        ? assessments.map(assessment => assessment.name).join(', ')
        : '—';

const formatClassification = classification => {
    if (!classification || !classification.name) {
        return '—';
    }
    return translate(classification.name);
};

export const formatCSVRow = row => {
    return {
        name: formatName(row.name),
        status: formatStatus(row.status, row.agent?.provider?.type),
        organizations: formatOrganizationsColumn(row.organizations),
        owners: formatOwners(row.owners),
        sourceConnections: formatSources(row.sourceConnections),
        assessments: formatAssessments(row.assessments),
        actionsToComplete: formatCompletion(row.actionsToComplete),
    };
};

export const formatFieldsCSVRow = row => {
    return {
        name: formatName(row.name),
        classification: formatClassification(row.classification),
        path: formatName(row.path),
        notes: formatName(row.notes),
    };
};
